import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';

const SourceFormModal = ({ show, onHide, datasetName, version, source, onSuccess, t, companyId = null }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    sourceType: '',
    geography: '',
    sector: '',
    primaryScope: '',
    validFrom: '',
    validTo: '',
    activityUnit: '',
    efBasis: '',
    datasetVersion: ''
  });
  const [errors, setErrors] = useState({});
  const isEdit = !!source;

  useEffect(() => {
    if (source) {
      setFormData({
        name: source.name || '',
        category: source.category || '',
        sourceType: source.sourceType || '',
        geography: source.geography || '',
        sector: source.sector || '',
        primaryScope: source.primaryScope || '',
        validFrom: source.validFrom ? source.validFrom.split('T')[0] : '',
        validTo: source.validTo ? source.validTo.split('T')[0] : '',
        activityUnit: source.activityUnit || '',
        efBasis: source.efBasis || '',
        datasetVersion: source.datasetVersion || ''
      });
    } else {
      setFormData({
        name: '',
        category: '',
        sourceType: '',
        geography: '',
        sector: '',
        primaryScope: '',
        validFrom: '',
        validTo: '',
        activityUnit: '',
        efBasis: '',
        datasetVersion: ''
      });
    }
    setErrors({});
  }, [source, show, datasetName]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t("SourceFormModal.errors.nameRequired.label");
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoaderDisplay(true);
    const payload = {
      ...formData,
      validFrom: formData.validFrom || null,
      validTo: formData.validTo || null
    };

    const promise = isEdit
      ? GHGEmissionMasterDataService.updateSource(datasetName, version, source.id, payload, companyId)
      : GHGEmissionMasterDataService.createSource(datasetName, version, payload, companyId);

    promise
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success(
            isEdit
              ? t("SourceFormModal.updateSuccess.label")
              : t("SourceFormModal.createSuccess.label"),
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
          onSuccess();
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered data-testid="source-form-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? t("SourceFormModal.editTitle.label") : t("SourceFormModal.addTitle.label")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              {t("SourceFormModal.name.label")} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              isInvalid={!!errors.name}
              data-testid="name-input"
            />
            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.category.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  data-testid="category-input"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.sourceType.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.sourceType}
                  onChange={(e) => handleChange('sourceType', e.target.value)}
                  data-testid="source-type-input"
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.geography.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.geography}
                  onChange={(e) => handleChange('geography', e.target.value)}
                  data-testid="geography-input"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.sector.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.sector}
                  onChange={(e) => handleChange('sector', e.target.value)}
                  data-testid="sector-input"
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.primaryScope.label")}</Form.Label>
                <Form.Select
                  value={formData.primaryScope}
                  onChange={(e) => handleChange('primaryScope', e.target.value)}
                  data-testid="primary-scope-input"
                >
                  <option value="">{t("SourceFormModal.primaryScope.select.label")}</option>
                  <option value="Scope 1">Scope 1</option>
                  <option value="Scope 2">Scope 2</option>
                  <option value="Scope 3">Scope 3</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.activityUnit.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.activityUnit}
                  onChange={(e) => handleChange('activityUnit', e.target.value)}
                  data-testid="activity-unit-input"
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.validFrom.label")}</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => handleChange('validFrom', e.target.value)}
                    data-testid="valid-from-input"
                  />
                  {formData.validFrom && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleChange('validFrom', '')}
                      data-testid="clear-valid-from"
                      title={t("Clear.label")}
                    >
                      <BiX />
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.validTo.label")}</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="date"
                    value={formData.validTo}
                    onChange={(e) => handleChange('validTo', e.target.value)}
                    data-testid="valid-to-input"
                  />
                  {formData.validTo && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleChange('validTo', '')}
                      data-testid="clear-valid-to"
                      title={t("Clear.label")}
                    >
                      <BiX />
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.efBasis.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.efBasis}
                  onChange={(e) => handleChange('efBasis', e.target.value)}
                  data-testid="ef-basis-input"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("SourceFormModal.datasetVersion.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.datasetVersion}
                  onChange={(e) => handleChange('datasetVersion', e.target.value)}
                  data-testid="dataset-version-input"
                />
              </Form.Group>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("Cancel.label")}
        </Button>
        <Button variant="primary" onClick={handleSubmit} data-testid="submit-button">
          {isEdit ? t("Update.label") : t("Create.label")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withTranslation()(SourceFormModal);
